// Updated Calendar Component with Better State Management, Memoization, and Type Handling

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format, addDays, isSameDay, isWithinInterval, eachDayOfInterval, parseISO, isAfter, isBefore, getDay, differenceInDays } from 'date-fns';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg, EventInput } from '@fullcalendar/core';
import axiosInstance from '../utils/axiosConfig';
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WhosWorkingToday from './WhosWorkingToday';
import { DarkDate } from '../types/mongodb';

// Constants
const WORKING_STATUSES = ['working', 'programming', 'event', 'am', 'pm', 'cooler', 'abroad'] as const;
type WorkingStatus = typeof WORKING_STATUSES[number];

const STAFF_STATUS_COLORS: Record<string, string> = {
  'working': '#ec4899',
  'programming': '#8b5cf6',  
  'event': '#a855f7',
  'am': '#059669',
  'pm': '#84cc16',
  'cooler': '#60a5fa',
  'abroad': '#2563eb',
  'traveling': '#eab308',
  'company-leave': '#14b8a6',
};

const EVENT_TYPE_COLORS: Record<string, string> = {
  'performance': '#2196F3',
  'strand': '#9C27B0',
  'event': '#4CAF50',
  'prospect': '#FF9800',
  'staff': '#ec4899',
  'dark': '#9e9e9e',
};

const STATUS_COLORS: Record<string, string> = {
  'confirmed': '#4CAF50',
  'cancelled': '#F44336',
  'planned': '#FFC107',
};

// Type definitions
interface BandReference {
  artistName: string;
  _id: string;
}

interface SimpleBand {
  simpleBandName: string;
}

type BandType = string | BandReference | SimpleBand;

interface Performance {
  _id: string;
  band?: BandType;
  simpleBandName?: string;
  event: string;
  startTime: string;
  endTime: string;
  fee: number;
  status: string;
  ticketPrice: number;
  sponsorship: number;
  notes: string;
}

interface Event {
  _id: string;
  name: string;
  eventType: string;
  description: string;
  date?: string;
  startDate?: string;
  endDate?: string;
  status: string;
}

interface Strand {
  _id: string;
  name: string;
  description: string;
  type: string;
  year: number;
  startDate: string;
  endDate: string;
  eventType: string;
  status: string;
  published: boolean;
  slug: string;
}

interface ArtistReference {
  artistName: string;
  _id: string;
}

interface Prospect {
  _id: string;
  strandId: string;
  date: string;
  eventConcept: string;
  potentialArtists: Array<ArtistReference | string>;
  status: string;
  fees: number;
  notes: string;
}

interface Personnel {
  _id: string;
  name: string;
  email: string;
  roles: string[];
  profileImage?: string;
}

interface PopulatedRotaEntry {
  _id: string;
  personnelId: Personnel;
  date: string;
  startTime: string;
  endTime: string;
  status: WorkingStatus;
  notes?: string;
  statusLabel?: string;
}

interface UnpopulatedRotaEntry {
  _id: string;
  personnelId: string;
  date: string;
  startTime: string;
  endTime: string;
  status: WorkingStatus;
  notes?: string;
  statusLabel?: string;
}

type RotaEntry = PopulatedRotaEntry | UnpopulatedRotaEntry;

interface CalendarEvent extends EventInput {
  type: 'event' | 'performance' | 'prospect' | 'strand' | 'staff' | 'dark';
  originalId: string;
  eventType?: string;
  status?: string;
  strandId?: string;
  assignedStaff?: Array<{ name: string; role: string }>;
  staffMember?: Personnel;
}

interface Filters {
  showPerformances: boolean;
  showStrands: boolean;
  showEvents: boolean;
  showProspects: boolean;
  showStaff: boolean;
  hideMultiDay: boolean;
  status: string;
  eventType: string;
  strandId: string;
  staffId: string;
}

interface LoadingStates {
  strands: boolean;
  events: boolean;
  performances: boolean;
  prospects: boolean;
  rota: boolean;
}

interface ErrorStates {
  strands: string | null;
  events: string | null;
  performances: string | null;
  prospects: string | null;
  rota: string | null;
}

// Type guard functions
function isPopulatedRotaEntry(entry: RotaEntry): entry is PopulatedRotaEntry {
  return typeof entry.personnelId === 'object' && entry.personnelId !== null;
}

function isBandReference(band: BandType): band is BandReference {
  return typeof band === 'object' && 'artistName' in band;
}

function isSimpleBand(band: BandType): band is SimpleBand {
  return typeof band === 'object' && 'simpleBandName' in band;
}

function isArtistReference(artist: ArtistReference | string): artist is ArtistReference {
  return typeof artist === 'object' && 'artistName' in artist;
}

const CalendarComponent: React.FC = () => {
  const navigate = useNavigate();
  
  // State management
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [strands, setStrands] = useState<Strand[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [rotaEntries, setRotaEntries] = useState<RotaEntry[]>([]);
  const [darkDates, setDarkDates] = useState<DarkDate[]>([]);

  
  const [loadingStates, setLoadingStates] = useState<LoadingStates>({
    strands: true,
    events: true,
    performances: true,
    prospects: true,
    rota: true,
  });
  
  const [errorStates, setErrorStates] = useState<ErrorStates>({
    strands: null,
    events: null,
    performances: null,
    prospects: null,
    rota: null,
  });
  
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);
  
  const [filters, setFilters] = useState<Filters>({
    showPerformances: true,
    showStrands: false,
    showEvents: false,
    showProspects: true,
    showStaff: false,
    hideMultiDay: false,
    status: 'all',
    eventType: 'all',
    strandId: 'all',
    staffId: 'all'
  });

  // Utility functions - memoized with useCallback
  const getBandName = useCallback((band: BandType | undefined): string => {
    if (!band) return 'Unnamed Band';
    if (typeof band === 'string') return band;
    if (isSimpleBand(band)) return band.simpleBandName;
    if (isBandReference(band)) return band.artistName;
    return 'Unnamed Band';
  }, []);

  const getStaffStatusColor = useCallback((status: string): string => {
    return STAFF_STATUS_COLORS[status] || '#6b7280';
  }, []);

  const getStatusColor = useCallback((status: string): string => {
    return STATUS_COLORS[status.toLowerCase()] || '#757575';
  }, []);

  const isMultiDayEvent = useCallback((event: CalendarEvent): boolean => {
    if (!event.start || !event.end) return false;
    
    try {
      // Handle DateInput type from FullCalendar
      let startDate: Date;
      let endDate: Date;
      
      if (event.start instanceof Date) {
        startDate = event.start;
      } else if (typeof event.start === 'string' || typeof event.start === 'number') {
        startDate = new Date(event.start);
      } else {
        return false;
      }
      
      if (event.end instanceof Date) {
        endDate = event.end;
      } else if (typeof event.end === 'string' || typeof event.end === 'number') {
        endDate = new Date(event.end);
      } else {
        return false;
      }
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return false;
      }
      
      if (event.allDay) {
        return differenceInDays(endDate, startDate) > 1;
      }
      
      return !isSameDay(startDate, endDate);
    } catch {
      return false;
    }
  }, []);

  const normalizeDate = useCallback((dateStr: any): string => {
    if (typeof dateStr !== 'string') {
      return new Date(dateStr).toISOString().split('T')[0];
    }
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    return dateStr;
  }, []);

  // Data fetching functions
  const fetchRotaData = useCallback(async () => {
    setLoadingStates(prev => ({ ...prev, rota: true }));
    setErrorStates(prev => ({ ...prev, rota: null }));
    
    try {
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const [personnelResponse, rotaResponse] = await Promise.all([
        axiosInstance.get<Personnel[]>('/api/rota/personnel'),
        axiosInstance.get<RotaEntry[]>('/api/rota', {
          params: {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          }
        })
      ]);

      setPersonnel(personnelResponse.data);
      setRotaEntries(rotaResponse.data);
      return rotaResponse.data;
    } catch (error) {
      console.error('Error fetching rota data:', error);
      setErrorStates(prev => ({ ...prev, rota: 'Failed to fetch staff rota data' }));
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, rota: false }));
    }
  }, []);

  const fetchStrands = useCallback(async () => {
    setLoadingStates(prev => ({ ...prev, strands: true }));
    setErrorStates(prev => ({ ...prev, strands: null }));
    
    try {
      const response = await axiosInstance.get<Strand[]>('/api/strands');
      setStrands(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching strands:', error);
      setErrorStates(prev => ({ ...prev, strands: 'Failed to fetch strands' }));
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, strands: false }));
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoadingStates(prev => ({ ...prev, events: true }));
    setErrorStates(prev => ({ ...prev, events: null }));
    
    try {
      const response = await axiosInstance.get<Event[]>('/api/events');
      return response.data;
    } catch (error) {
      console.error('Error fetching events:', error);
      setErrorStates(prev => ({ ...prev, events: 'Failed to fetch events' }));
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, events: false }));
    }
  }, []);

  const fetchPerformances = useCallback(async () => {
    setLoadingStates(prev => ({ ...prev, performances: true }));
    setErrorStates(prev => ({ ...prev, performances: null }));
    
    try {
      const response = await axiosInstance.get<Performance[]>('/api/performances');
      return response.data;
    } catch (error) {
      console.error('Error fetching performances:', error);
      setErrorStates(prev => ({ ...prev, performances: 'Failed to fetch performances' }));
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, performances: false }));
    }
  }, []);

 const fetchDarkDates = useCallback(async () => {
  try {
    const today = new Date();
    const startDate = new Date(today.getFullYear() - 1, 0, 1);
    const endDate = new Date(today.getFullYear() + 1, 11, 31);
    
    const response = await axiosInstance.get<DarkDate[]>('/api/dark-dates/range', {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching dark dates:', error);
    return [];
  }
}, []);

  const fetchProspects = useCallback(async () => {
    setLoadingStates(prev => ({ ...prev, prospects: true }));
    setErrorStates(prev => ({ ...prev, prospects: null }));
    
    try {
      const response = await axiosInstance.get<Prospect[]>('/api/strands/prospects');
      return response.data;
    } catch (error) {
      console.error('Error fetching prospects:', error);
      setErrorStates(prev => ({ ...prev, prospects: 'Failed to fetch prospects' }));
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, prospects: false }));
    }
  }, []);

  const mapStrandsToEvents = useCallback((strandsData: Strand[]): CalendarEvent[] => {
    return strandsData.map(strand => ({
      id: `strand-${strand._id}`,
      title: strand.name,
      start: strand.startDate,
      end: strand.endDate,
      type: 'strand' as const,
      originalId: strand._id,
      eventType: strand.eventType,
      status: strand.status,
      backgroundColor: EVENT_TYPE_COLORS.strand,
      extendedProps: {
        description: strand.description,
        type: 'strand',
        published: strand.published
      }
    }));
  }, []);

const mapDarkDatesToEvents = useCallback((darkDatesData: DarkDate[]): CalendarEvent[] => {
  return darkDatesData.map(darkDate => ({
    id: `dark-${darkDate._id}`,
    title: `CLOSED: ${darkDate.reason}`,
    start: darkDate.date,
    allDay: true,
    type: 'dark' as const,
    originalId: darkDate._id,
    backgroundColor: '#f59e0b',
    borderColor: '#f59e0b',
    classNames: ['dark-date-event'],
    extendedProps: {
      type: 'dark',
      category: darkDate.category,
      notes: darkDate.notes
    }
  }));
}, []);

  const mapEventsToCalendar = useCallback((eventsData: Event[]): CalendarEvent[] => {
    return eventsData.map(event => ({
      id: `event-${event._id}`,
      title: event.name,
      start: event.startDate || event.date,
      end: event.endDate || event.date,
      type: 'event' as const,
      originalId: event._id,
      eventType: event.eventType,
      status: event.status,
      backgroundColor: EVENT_TYPE_COLORS.event,
      extendedProps: {
        type: 'event',
        description: event.description
      }
    }));
  }, []);

  const mapPerformancesToEvents = useCallback((performancesData: Performance[]): CalendarEvent[] => {
    return performancesData.map(performance => {
      const bandName = performance.simpleBandName || getBandName(performance.band);
      
      return {
        id: `performance-${performance._id}`,
        title: bandName,
        start: performance.startTime,
        end: performance.endTime,
        type: 'performance' as const,
        originalId: performance._id,
        status: performance.status,
        backgroundColor: EVENT_TYPE_COLORS.performance,
        extendedProps: {
          type: 'performance',
          fee: performance.fee,
          ticketPrice: performance.ticketPrice,
          notes: performance.notes
        }
      };
    });
  }, [getBandName]);

  const mapProspectsToEvents = useCallback((prospectsData: Prospect[]): CalendarEvent[] => {
    return prospectsData.map(prospect => {
      let artistName = 'Unknown';
      
      if (Array.isArray(prospect.potentialArtists) && prospect.potentialArtists.length > 0) {
        const firstArtist = prospect.potentialArtists[0];
        artistName = typeof firstArtist === 'string' 
          ? firstArtist 
          : (isArtistReference(firstArtist) ? firstArtist.artistName : 'Unknown');
      }

      return {
        id: `prospect-${prospect._id}`,
        title: artistName,
        start: prospect.date,
        type: 'prospect' as const,
        originalId: prospect._id,
        status: prospect.status,
        backgroundColor: EVENT_TYPE_COLORS.prospect,
        extendedProps: {
          type: 'prospect',
          eventConcept: prospect.eventConcept,
          strandId: prospect.strandId,
          fees: prospect.fees,
          notes: prospect.notes
        }
      };
    });
  }, []);

  const mapRotaToEvents = useCallback((rotaData: RotaEntry[], personnelData: Personnel[]): CalendarEvent[] => {
    const mappedEvents: CalendarEvent[] = [];
    
    rotaData
      .filter(entry => WORKING_STATUSES.includes(entry.status as WorkingStatus))
      .forEach(entry => {
        let personnelObj: Personnel | undefined;
        let personnelName: string;

        if (isPopulatedRotaEntry(entry)) {
          personnelObj = entry.personnelId;
          personnelName = entry.personnelId.name;
        } else {
          personnelObj = personnelData.find(p => p._id === entry.personnelId);
          personnelName = personnelObj?.name || 'Unknown Staff';
        }

        if (!personnelObj) return;

        const dateStr = normalizeDate(entry.date);
        const startDateTime = `${dateStr}T${entry.startTime}:00`;
        const endDateTime = `${dateStr}T${entry.endTime}:00`;

        mappedEvents.push({
          id: `staff-${entry._id}`,
          title: `${personnelName} - ${entry.status.toUpperCase()}`,
          start: startDateTime,
          end: endDateTime,
          type: 'staff' as const,
          originalId: entry._id,
          status: entry.status,
          backgroundColor: getStaffStatusColor(entry.status),
          textColor: '#ffffff',
          staffMember: personnelObj,
          extendedProps: {
            type: 'staff',
            status: entry.status,
            startTime: entry.startTime,
            endTime: entry.endTime,
            notes: entry.notes || '',
            staffName: personnelName,
            statusLabel: entry.status
          }
        });
      });
    
    return mappedEvents;
  }, [getStaffStatusColor, normalizeDate]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [strandsData, eventsData, performancesData, prospectsData, rotaData, darkDatesData] = await Promise.all([
  fetchStrands(),
  fetchEvents(),
  fetchPerformances(),
  fetchProspects(),
  fetchRotaData(),
  fetchDarkDates()
]);
console.log('Dark dates fetched:', darkDatesData);
console.log('Mapped dark events:', mapDarkDatesToEvents(darkDatesData));

       const mappedEvents: CalendarEvent[] = [
  ...mapStrandsToEvents(strandsData),
  ...mapEventsToCalendar(eventsData),
  ...mapPerformancesToEvents(performancesData),
  ...mapProspectsToEvents(prospectsData),
  ...mapRotaToEvents(rotaData, personnel),
  ...mapDarkDatesToEvents(darkDatesData)
];

        setEvents(mappedEvents);
      } catch (error) {
        console.error('Error in fetchAllData:', error);
      }
    };

    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoized filtered events
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (!filters.showPerformances && event.type === 'performance') return false;
      if (!filters.showStrands && event.type === 'strand') return false;
      if (!filters.showEvents && event.type === 'event') return false;
      if (!filters.showProspects && event.type === 'prospect') return false;
      if (!filters.showStaff && event.type === 'staff') return false;
      if (filters.hideMultiDay && isMultiDayEvent(event)) return false;
      if (filters.status !== 'all' && event.status !== filters.status) return false;
      if (filters.eventType !== 'all' && event.eventType !== filters.eventType) return false;
      if (filters.strandId !== 'all' && event.extendedProps?.strandId !== filters.strandId) return false;
      if (filters.staffId !== 'all' && event.type === 'staff' && event.staffMember?._id !== filters.staffId) return false;
      return true;
    });
  }, [events, filters, isMultiDayEvent]);

  // Memoized event handlers
  const handleEventClick = useCallback((clickInfo: EventClickArg) => {
    const eventData = clickInfo.event;
    const eventId = eventData.id;
    const eventType = eventData.extendedProps.type;
    
    // Extract the actual ID from the prefixed format
    const originalId = eventId.includes('-') ? eventId.split('-').slice(1).join('-') : eventId;
    
    setSelectedEvent({
      ...eventData.toPlainObject(),
      type: eventType,
      originalId: originalId
    });
    setIsEventDetailsOpen(true);
  }, []);

  const handleViewDetails = useCallback(() => {
    if (!selectedEvent) return;

    console.log('Navigating to:', selectedEvent.type, 'ID:', selectedEvent.originalId);

    if (selectedEvent.type === 'staff') {
      navigate('/admin/rota?scroll=calendar');
      setIsEventDetailsOpen(false);
      return;
    }

    const routeMap: Record<string, string> = {
      'strand': '/admin/strands',
      'event': '/admin/events',
      'performance': '/admin/performances',
      'prospect': '/admin/prospects'
    };

    const baseRoute = routeMap[selectedEvent.type];
    
    if (baseRoute) {
      navigate(`${baseRoute}/${selectedEvent.originalId}`);
      setIsEventDetailsOpen(false);
    } else {
      console.error('Unknown event type:', selectedEvent.type);
    }
  }, [selectedEvent, navigate]);

  const handleCloseDialog = useCallback(() => {
    setIsEventDetailsOpen(false);
  }, []);

  // Helper to get initials
  const getInitials = useCallback((name: string): string => {
    return name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase())
      .join('');
  }, []);

  // Memoized render function
  const renderEventContent = useCallback((eventInfo: any) => {
      if (eventInfo.event.extendedProps.type === 'dark') {
    return (
      <Box sx={{ p: 1, color: '#000000', fontWeight: 'bold' }}>
        <Typography variant="body2" sx={{ color: '#000000' }} noWrap>
          {eventInfo.event.title}
        </Typography>
      </Box>
    );
  }
    const viewType = eventInfo.view.type;
    const isStaff = eventInfo.event.extendedProps.type === 'staff';
    
    let displayTitle = eventInfo.event.title;
    
    // Format staff events based on view
    if (isStaff && eventInfo.event.extendedProps.staffName) {
      const name = eventInfo.event.extendedProps.staffName;
      const initials = getInitials(name);
      
      if (viewType === 'dayGridMonth') {
        displayTitle = initials;
      } else if (viewType === 'timeGridWeek') {
        const firstName = name.split(' ')[0];
        displayTitle = firstName;
      }
      // timeGridDay keeps full title
    }
    
    return (
      <Box sx={{ p: 1 }}>
        <Typography variant="body2" noWrap>
          {displayTitle}
        </Typography>
        {eventInfo.event.extendedProps.type && viewType !== 'dayGridMonth' && (
          <Chip
            label={eventInfo.event.extendedProps.type}
            size="small"
            sx={{
              mr: 1,
              backgroundColor: eventInfo.event.backgroundColor || 'default',
              color: '#fff',
              fontSize: '0.6rem',
              height: '16px'
            }}
          />
        )}
        {isStaff && eventInfo.event.extendedProps.startTime && viewType === 'timeGridDay' && (
          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', opacity: 0.9 }}>
            {eventInfo.event.extendedProps.startTime}-{eventInfo.event.extendedProps.endTime}
          </Typography>
        )}
      </Box>
    );
  }, [getInitials]);

  // Memoized filter handlers
  const handleFilterChange = useCallback((filterName: keyof Filters, value: boolean | string) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  }, []);

  // Loading and error states
  const isLoading = Object.values(loadingStates).some(state => state);
  const hasErrors = Object.values(errorStates).some(error => error !== null);
  const errorMessages = Object.entries(errorStates)
    .filter(([_, error]) => error !== null)
    .map(([key, error]) => `${key}: ${error}`);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
    console.log('Dark events in filtered list:', filteredEvents.filter(e => e.extendedProps?.type === 'dark'));


  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Events Calendar
      </Typography>

      <WhosWorkingToday variant="compact" />

      {hasErrors && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some data failed to load: {errorMessages.join(', ')}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Filters
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showPerformances}
                  onChange={(e) => handleFilterChange('showPerformances', e.target.checked)}
                />
              }
              label="Performances"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showStrands}
                  onChange={(e) => handleFilterChange('showStrands', e.target.checked)}
                />
              }
              label="Strands"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showEvents}
                  onChange={(e) => handleFilterChange('showEvents', e.target.checked)}
                />
              }
              label="Events"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showProspects}
                  onChange={(e) => handleFilterChange('showProspects', e.target.checked)}
                />
              }
              label="Prospects"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showStaff}
                  onChange={(e) => handleFilterChange('showStaff', e.target.checked)}
                />
              }
              label="Staff"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.hideMultiDay}
                  onChange={(e) => handleFilterChange('hideMultiDay', e.target.checked)}
                />
              }
              label="Hide Multi-Day"
            />
          </Grid>

          {filters.showStaff && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Staff Member</InputLabel>
                <Select
                  value={filters.staffId}
                  onChange={(e) => handleFilterChange('staffId', e.target.value)}
                  label="Staff Member"
                >
                  <MenuItem value="all">All Staff</MenuItem>
                  {personnel.map(person => (
                    <MenuItem key={person._id} value={person._id}>
                      {person.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="planned">Planned</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={filters.eventType}
                onChange={(e) => handleFilterChange('eventType', e.target.value)}
                label="Event Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="Workshop">Workshop</MenuItem>
                <MenuItem value="Concert">Concert</MenuItem>
                <MenuItem value="Festival">Festival</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Strand</InputLabel>
              <Select
                value={filters.strandId}
                onChange={(e) => handleFilterChange('strandId', e.target.value)}
                label="Strand"
              >
                <MenuItem value="all">All Strands</MenuItem>
                {strands.map(strand => (
                  <MenuItem key={strand._id} value={strand._id}>
                    {strand.name} - ({strand.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay'
        }}
        events={filteredEvents}
        eventClick={handleEventClick}
        eventContent={renderEventContent}
        height="800px"
        timeZone="local"
        slotMinTime="08:00:00"
        slotMaxTime="24:00:00"
        displayEventTime={false}
        displayEventEnd={false}
      />

      <Dialog
        open={isEventDetailsOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedEvent?.title}
          <Chip
            label={selectedEvent?.type}
            size="small"
            sx={{ ml: 1 }}
          />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {selectedEvent?.status && (
              <Grid item xs={12}>
                <Typography>
                  <strong>Status:</strong>{' '}
                  <Chip 
                    label={selectedEvent.status}
                    size="small"
                    sx={{ 
                      backgroundColor: getStatusColor(selectedEvent.status),
                      color: '#fff'
                    }}
                  />
                </Typography>
              </Grid>
            )}
            
            {selectedEvent?.type === 'staff' && (
              <>
                <Grid item xs={12}>
                  <Typography><strong>Staff Member:</strong> {selectedEvent.extendedProps?.staffName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>Start Time:</strong> {selectedEvent.extendedProps?.startTime}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>End Time:</strong> {selectedEvent.extendedProps?.endTime}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Schedule Type:</strong> {selectedEvent.extendedProps?.statusLabel || selectedEvent.status}</Typography>
                </Grid>
              </>
            )}

            {selectedEvent?.type === 'performance' && (
              <>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>Fee:</strong> €{selectedEvent.extendedProps?.fee}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography><strong>Ticket Price:</strong> €{selectedEvent.extendedProps?.ticketPrice}</Typography>
                </Grid>
              </>
            )}

            {selectedEvent?.type === 'prospect' && (
              <>
                <Grid item xs={12}>
                  <Typography><strong>Event Concept:</strong> {selectedEvent.extendedProps?.eventConcept}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography><strong>Estimated Fees:</strong> €{selectedEvent.extendedProps?.fees}</Typography>
                </Grid>
              </>
            )}

            {selectedEvent?.extendedProps?.description && (
              <Grid item xs={12}>
                <Typography><strong>Description:</strong> {selectedEvent.extendedProps.description}</Typography>
              </Grid>
            )}
            
            {selectedEvent?.extendedProps?.notes && (
              <Grid item xs={12}>
                <Typography><strong>Notes:</strong> {selectedEvent.extendedProps.notes}</Typography>
              </Grid>
            )}

            {selectedEvent?.start && (
              <Grid item xs={12} sm={6}>
                <Typography>
                  <strong>Start:</strong>{' '}
                  {(() => {
                    const start = selectedEvent.start;
                    if (start instanceof Date) {
                      return start.toLocaleString();
                    } else if (typeof start === 'string' || typeof start === 'number') {
                      return new Date(start).toLocaleString();
                    }
                    return 'N/A';
                  })()}
                </Typography>
              </Grid>
            )}
            
            {selectedEvent?.end && (
              <Grid item xs={12} sm={6}>
                <Typography>
                  <strong>End:</strong>{' '}
                  {(() => {
                    const end = selectedEvent.end;
                    if (end instanceof Date) {
                      return end.toLocaleString();
                    } else if (typeof end === 'string' || typeof end === 'number') {
                      return new Date(end).toLocaleString();
                    }
                    return 'N/A';
                  })()}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button 
            onClick={handleViewDetails} 
            variant="contained" 
            color="primary"
          >
            {selectedEvent?.type === 'staff' ? 'View Roster' : 'View Full Details'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Legend
        </Typography>
        <Grid container spacing={2}>
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: EVENT_TYPE_COLORS.performance, mr: 1 }} />
              <Typography>Performances</Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: EVENT_TYPE_COLORS.strand, mr: 1 }} />
              <Typography>Strands</Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: EVENT_TYPE_COLORS.event, mr: 1 }} />
              <Typography>Events</Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: EVENT_TYPE_COLORS.prospect, mr: 1 }} />
              <Typography>Prospects</Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: EVENT_TYPE_COLORS.staff, mr: 1 }} />
              <Typography>Staff</Typography>
            </Box>
          </Grid>
          <Grid item>
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <Box sx={{ width: 16, height: 16, backgroundColor: EVENT_TYPE_COLORS.dark, mr: 1 }} />
    <Typography>Venue Closed</Typography>
  </Box>
</Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CalendarComponent;